import { useQuery } from "@tanstack/solid-query";
import type { TovProfileResponse } from "contracts/api/client-tov/responses";
import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import { TOV_VERSION_STATUS } from "contracts/enums/tov";
import { type Accessor, createMemo } from "solid-js";
import { QUERY_ROOT } from "../../enums/shell";
import { api } from "../../store/api";
import { SOURCE_POLL_MS } from "../../store/pollCadence";
import { isCascadeActive } from "../tov/statusLabels";

// Shared read chain for Client TOV: profiles → version → sources.
// One store per view; TanStack Query dedupes the underlying fetches across
// every consumer (Scan summary preview, quick look, Brand Guidelines).
//
// A profile has no currentVersionId until its first activation. Until then the
// newest draft is loaded as a PENDING version — its sources and failed map
// attempt must stay visible for recovery — and hasActiveVersion tells readers
// apart guidance actually used for assessment from setup still in progress.
type TovContext = {
  activeProfile: Accessor<TovProfileResponse | null>;
  version: Accessor<TovVersionResponse | null>;
  versionId: Accessor<string | undefined>;
  hasActiveVersion: Accessor<boolean>;
  sources: Accessor<TovSourceResponse[] | null>;
};

export function createTovStore(
  clientId: Accessor<string | undefined>,
): TovContext {
  const profiles = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_PROFILES, clientId()],
    queryFn: () => api.tov.listProfiles(clientId() as string),
    enabled: clientId() !== undefined,
  }));
  const activeProfile = createMemo(
    () =>
      profiles.data?.profiles.find((profile) => profile.currentVersionId) ??
      profiles.data?.profiles[0] ??
      null,
  );
  const activeVersionId = () => activeProfile()?.currentVersionId ?? undefined;
  const draftVersions = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_VERSIONS, clientId(), activeProfile()?.id],
    queryFn: () =>
      api.tov.listVersions(clientId() as string, activeProfile()?.id as string),
    enabled:
      clientId() !== undefined &&
      activeProfile() !== null &&
      activeVersionId() === undefined,
  }));
  const pendingVersionId = createMemo(() =>
    activeVersionId() === undefined
      ? draftVersions.data?.versions.find(
          (candidate) => candidate.status === TOV_VERSION_STATUS.DRAFT,
        )?.id
      : undefined,
  );
  const versionId = () => activeVersionId() ?? pendingVersionId();
  const version = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_VERSION, clientId(), versionId()],
    queryFn: () =>
      api.tov.getVersion(clientId() as string, versionId() as string),
    enabled: clientId() !== undefined && versionId() !== undefined,
  }));
  const sources = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_SOURCES, clientId(), version.data?.id],
    queryFn: () =>
      api.tov.listSources(clientId() as string, version.data?.id as string),
    enabled: clientId() !== undefined && version.data !== undefined,
    // Read-only surfaces used to freeze mid-cascade until a manual reload: a
    // user who uploads and navigates here saw a stale "1/2 parsed" forever.
    refetchInterval: (current) =>
      isCascadeActive(current.state.data?.sources ?? [])
        ? SOURCE_POLL_MS
        : false,
  }));

  return {
    activeProfile,
    version: () => version.data ?? null,
    versionId,
    hasActiveVersion: () => activeVersionId() !== undefined,
    sources: () => sources.data?.sources ?? null,
  };
}
